import {
  ImportExportHistoryColumn,
  OnCancelClick,
  OnDetailsClick,
  OnDownloadClick,
} from '@/components/ImportExportHistory/type';
import { ErrorBoundary } from '@/components/Rollbar';
import { Table, TableProps } from '@moneyforward/ap-frontend-components';
import { FC, Suspense, memo, useMemo } from 'react';
import { useImportExportHistoryColumns } from './Columns/column';

export type ImportExportHistoryTableProps = Omit<
  TableProps<ImportExportHistoryColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

export type ActionHandlers = {
  onDownloadClick: OnDownloadClick;
  onCancelClick: OnCancelClick;
  onDetailsClick?: OnDetailsClick;
};

type Props = {
  table: ImportExportHistoryTableProps;
  actionHandlers?: ActionHandlers;
};

export const ImportExportHistoryTable: FC<Props> = ({
  table,
  actionHandlers,
}) => {
  const columns = useImportExportHistoryColumns(actionHandlers);
  const hiddenPagination = useMemo(
    () => ({
      currentPage: 1,
      maxPage: 1,
      perPage: 1,
      maxRecord: 0,
    }),
    []
  );

  return (
    <Table
      emptyType='file'
      {...table}
      legacy={false}
      columns={columns}
      pagination={hiddenPagination}
      hideHeaderPagination
      hideFooterPagination
      rowKey='id'
      testId='import-export-history-table'
    />
  );
};
ImportExportHistoryTable.displayName = 'ImportExportHistoryTable';

type ErrorImportExportHistoryTableProps = {
  error: Error | null;
};

export const ImportExportHistoryTableError: FC<ErrorImportExportHistoryTableProps> =
  memo(() => {
    const tableProps = useMemo(
      () => ({ data: [], loading: false, emptyType: 'error' }),
      []
    );
    return <ImportExportHistoryTable table={tableProps} />;
  });
ImportExportHistoryTableError.displayName = 'ImportExportHistoryTableError';

export const ImportExportHistoryTableLoading: FC = memo(() => {
  const tableProps = useMemo(() => ({ data: [], loading: true }), []);
  return <ImportExportHistoryTable table={tableProps} />;
});
ImportExportHistoryTableLoading.displayName = 'ImportExportHistoryTableLoading';

type ImportExportHistoryTableViewProps = {
  table: ImportExportHistoryTableProps;
  actionHandlers?: ActionHandlers;
};

export const ImportExportHistoryTableView: FC<ImportExportHistoryTableViewProps> =
  memo(({ table, actionHandlers }) => {
    return (
      <ErrorBoundary fallback={ImportExportHistoryTableError}>
        <Suspense fallback={<ImportExportHistoryTableLoading />}>
          <ImportExportHistoryTable
            table={table}
            actionHandlers={actionHandlers}
          />
        </Suspense>
      </ErrorBoundary>
    );
  });
ImportExportHistoryTableView.displayName = 'ImportExportHistoryTableView';
